import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useForm } from '@weezevent/weezjs-core';
import useAppContext from 'context/useAppContext';
import { BrandKit } from 'models';
import { getLanguagesOptions, getLocale } from 'utils/languages';

import { WeezHeader, FrameCard, Button, ToggleLabel, InputLabel, Input, Form, ToastContext, TranslateDropdown } from '@weezevent/nacre';

import { BrandKitBody, ColorSection } from './components';
import { LoadingCard } from 'components';

import css from '../templates.module.css';

const COLOR_FIELDS = ['primary_color', 'background_color', 'container_background_color'];

const Brandkit = ({ id, organizationId }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [{ organization }] = useAppContext();
    const toast = React.useContext(ToastContext);
    const defaultLocale = getLocale(organization?.default_locale);

    const [selectedLanguage, setSelectedLanguage] = React.useState(() => defaultLocale);

    const [form, setForm, setFormObject] = useForm({
        id,
        name: '',
        headers: [],
        footers: [],
        enabled: true,
        primary_color: null,
        background_color: null,
        organization: organizationId,
        container_background_color: null
    });

    const [, loadingBrandKit] = BrandKit.useApiModel(
        {
            organizationId,
            id,
            launch: Boolean(id),
            onSuccess: ({ object }) => setFormObject({ ...object, headers: object.headers ?? [], footers: object.footers ?? [] }, true)
        },
        [id, organizationId]
    );

    const backLink = React.useMemo(() => {
        if (!organizationId) {
            return '/emails/templates/brandkits';
        }
        return `/organizations/O${organizationId}/emails/templates/brandkits`;
    }, [organizationId]);

    const selectedLanguages = React.useMemo(() => {
        return Array.from(new Set([...form.headers, ...form.footers].map(bd => bd.lang)));
    }, [form.headers, form.footers]);

    const handleReset = (key, value, fallback) => {
        if (!value) {
            setForm(key, fallback);
        }
    };

    const handleSubmit = React.useCallback(() => {
        const brandKit = new BrandKit({ ...form });
        brandKit
            .save()
            .then(response => {
                if ([200, 201].includes(response.status)) {
                    history.push(backLink);
                }
            })
            .catch(() => toast.error(t('sauron.toasts.error')));
    }, [form, backLink]);

    return (
        <>
            <WeezHeader
                rightComponent={[<Button key="save" primary inverted label={t('common.cta.save')} onClick={handleSubmit} disabled={false} />]}
                title={id ? form.name || ' ' : t('sauron.emails.templates.brandkits.new')}
                backLink={{
                    onClick: () => {
                        history.push(backLink);
                    }
                }}
            />
            <FrameCard title={t('sauron.emails.templates.brandkits.settings.title')} separator>
                <LoadingCard loading={id && loadingBrandKit}>
                    <ToggleLabel
                        inlinePosition="right"
                        label={t('sauron.emails.templates.brandkits.enable')}
                        checked={form.enabled}
                        onChange={({ target: { checked } }) => setForm('enabled', checked)}
                    />

                    <Form.Field>
                        <InputLabel label={t('sauron.emails.templates.brandkits.name')} />
                        <Input placeholder={t('sauron.emails.templates.brandkits.name')} value={form.name} onChange={({ target: { value } }) => setForm('name', value)} />
                    </Form.Field>

                    {COLOR_FIELDS.map((field, i) => (
                        <ColorSection key={`color_${field}_${i}`} form={form} setForm={setForm} colorField={field} handleReset={handleReset} />
                    ))}
                </LoadingCard>
            </FrameCard>

            <FrameCard
                title={
                    <div className={css['template-bodies-card']}>
                        <span>{t('sauron.emails.templates.brandkits.templates')}</span>
                        <div className={css['lang-select']}>
                            {(form.headers.length > 0 || form.footers.length > 0) && (
                                <TranslateDropdown defaultValue={defaultLocale} selected={selectedLanguages} onChange={setSelectedLanguage} languages={getLanguagesOptions()} />
                            )}
                        </div>
                    </div>
                }
                subtitle={t('sauron.emails.templates.brandkits.templates_subtitle')}
                separator
            >
                <LoadingCard loading={id && loadingBrandKit}>
                    <BrandKitBody
                        formKey="headers"
                        form={form}
                        setForm={setForm}
                        onClose={handleReset}
                        organizationId={organizationId}
                        selectedLanguage={selectedLanguage}
                        title={t('sauron.emails.templates.brandkits.settings.header')}
                        subtitle={t('sauron.emails.templates.brandkits.settings.header_subtitle')}
                    />
                    <BrandKitBody
                        formKey="footers"
                        form={form}
                        setForm={setForm}
                        onClose={handleReset}
                        organizationId={organizationId}
                        selectedLanguage={selectedLanguage}
                        title={t('sauron.emails.templates.brandkits.settings.footer')}
                        subtitle={t('sauron.emails.templates.brandkits.settings.footer_subtitle')}
                    />
                </LoadingCard>
            </FrameCard>
        </>
    );
};

export default Brandkit;
